import React, { useEffect, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const projectsImages = [
  "/assets/images/project1.png",
  "/assets/images/project2.png",
  "/assets/images/project3.jpg",
];

const projects = [
  {
    id: 1,
    title: "Model School Construction",
    body:
      "The state is constructing model schools to modernize foundational learning environments and improve classroom infrastructure across Akwa Ibom State.",
  },
  {
    id: 2,
    title: "UBEC Smart School — Obio Akpa",
    body:
      "A technology-driven education project focused on smart classrooms, ICT learning support, digital education delivery and modern teaching facilities.",
  },
  {
    id: 3,
    title: "Comprehensive Basic Education Reforms",
    body:
      "AKSUBEB is strengthening basic education through school rehabilitation, learning materials, teacher support and targeted infrastructure interventions.",
  },
];

const ProjectsSection = () => {
  const [imgIdx, setImgIdx] = useState(0);

  const total = projectsImages.length;

  const nextImg = useCallback(() => {
    if (!total) return;
    setImgIdx((i) => (i + 1) % total);
  }, [total]);

  const prevImg = () => {
    if (!total) return;
    setImgIdx((i) => (i - 1 + total) % total);
  };

  useEffect(() => {
    if (!total) return;

    const id = setInterval(nextImg, 5000);

    return () => clearInterval(id);
  }, [nextImg, total]);

  return (
    <section className="relative py-20 bg-white overflow-hidden">
      <div className="absolute -left-32 top-10 w-96 h-96 bg-[#ff6b00]/14 rounded-full blur-3xl pointer-events-none animate-pulse" />

      <div className="absolute -right-32 bottom-10 w-96 h-96 bg-[#06183f]/12 rounded-full blur-3xl pointer-events-none animate-pulse" />

      <div className="relative max-w-7xl mx-auto px-8 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="flex items-center gap-3 mb-5">
            <span className="w-10 h-[3px] bg-[#ff6b00]" />

            <span className="uppercase text-[#ff6b00] font-bold tracking-[0.2em] text-sm">
              Our Projects
            </span>
          </div>

          <h2
            className="text-4xl md:text-5xl lg:text-6xl font-black text-[#06183f] leading-[1.05] mb-7"
            style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
          >
            AKSUBEB’s Primary <br />

            <span className="relative inline-block text-[#06183f]">
              <span className="relative z-10">
                Initiatives & Project Focus Areas
              </span>

              <span className="absolute left-0 bottom-1 h-3 w-full bg-[#ff6b00]/25 rounded-full animate-pulse" />

              <span className="absolute -right-5 -top-4 w-4 h-4 rounded-full bg-[#ff6b00] animate-ping" />
            </span>
          </h2>

          <p className="text-slate-600 text-[17px] leading-[1.85] mb-8 max-w-2xl">
            AKSUBEB continues to strengthen educational development across Akwa
            Ibom State through strategic infrastructure projects, digital
            education systems, school rehabilitation programs, and comprehensive
            learning reforms.
          </p>

          <div className="space-y-4">
            {projects.map((project) => (
              <div
                key={project.id}
                className="group bg-white border border-slate-100 rounded-[26px] p-6 shadow-sm hover:shadow-[0_22px_55px_-12px_rgba(8,37,103,0.22)] transition-all duration-500 hover:-translate-y-2"
              >
                <div className="flex items-start gap-5">
                  <div className="w-14 h-14 rounded-2xl bg-[#06183f]/10 text-[#06183f] flex items-center justify-center flex-shrink-0 group-hover:bg-[#ff6b00] group-hover:text-white transition-all duration-500">
                    <span className="font-black text-xl">
                      {String(project.id).padStart(2, "0")}
                    </span>
                  </div>

                  <div>
                    <h3
                      className="text-2xl font-black text-[#06183f] mb-3"
                      style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                    >
                      {project.title}
                    </h3>

                    <p className="text-slate-600 leading-[1.85] text-[15.5px]">
                      {project.body}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <Link
            to="/projects"
            className="inline-flex items-center gap-2 mt-6 text-[#06183f] font-bold border-b-2 border-[#06183f] pb-1 hover:gap-3 hover:text-[#ff6b00] hover:border-[#ff6b00] transition-all"
          >
            View all projects <ArrowRight size={18} />
          </Link>
        </div>

        <div className="relative rounded-[32px] overflow-hidden min-h-[560px] bg-[#f7f9fc] shadow-[0_35px_90px_-30px_rgba(8,37,103,0.35)]">
          <div className="absolute -top-28 -right-28 w-96 h-96 bg-[#ff6b00]/14 rounded-full blur-3xl pointer-events-none animate-pulse" />

          <div className="absolute -bottom-28 -left-28 w-96 h-96 bg-[#06183f]/14 rounded-full blur-3xl pointer-events-none animate-pulse" />

          <div className="absolute inset-0 p-5">
            <img
              src={projectsImages[imgIdx]}
              alt="AKSUBEB project"
              className="w-full h-full object-cover rounded-[26px]"
            />

            <div className="absolute inset-5 rounded-[26px] bg-gradient-to-t from-black/45 via-black/10 to-transparent" />

            <div className="absolute left-10 bottom-10 right-10">
              <div className="inline-flex items-center px-5 py-2.5 rounded-full bg-white/90 text-[#06183f] text-xs font-black uppercase tracking-[0.2em] mb-5">
                Project Gallery
              </div>

              <h3
                className="text-white text-4xl md:text-5xl font-black leading-tight drop-shadow-lg"
                style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
              >
                Building Better Learning Spaces
              </h3>
            </div>
          </div>

          <div className="absolute right-8 bottom-8 flex items-center gap-2">
            <button
              type="button"
              onClick={prevImg}
              className="w-11 h-11 rounded-full bg-white/90 text-[#06183f] flex items-center justify-center hover:bg-[#ff6b00] hover:text-white transition-colors"
              aria-label="Previous project"
            >
              <ChevronLeft size={21} />
            </button>

            <button
              type="button"
              onClick={nextImg}
              className="w-11 h-11 rounded-full bg-white/90 text-[#06183f] flex items-center justify-center hover:bg-[#ff6b00] hover:text-white transition-colors"
              aria-label="Next project"
            >
              <ChevronRight size={21} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectsSection;